/**
 * ClockControl - start and stop tracking, from anywhere in the app.
 */
import { useEffect, useState } from "react";

import { message } from "antd";
import { Play, Pause, Square } from "lucide-react";

import {
  useClockInMutation,
  useClockOutMutation,
  useGetActiveShiftQuery,
  usePauseClockMutation,
  useResumeClockMutation,
} from "@/features/people/api/volunteerApi";
import { formatWorkedElapsed } from "@/features/people/lib/elapsed";
import { useVolunteerSelfClockAccess } from "@/features/people/model/useVolunteerSelfClockAccess";

function errorMessage(error: unknown, fallback: string): string {
  return (error as { data?: { detail?: string } })?.data?.detail ?? fallback;
}

function clockOutMessage(shift: { hours: string; is_overtime?: boolean; overtime_hours?: string | null }, elapsedLabel: string | null): string {
  const logged = Number.parseFloat(shift.hours).toFixed(2);
  const overtime = shift.is_overtime
    ? Number.parseFloat(shift.overtime_hours ?? "0")
    : 0;
  const overtimeNote =
    overtime > 0
      ? ` ${overtime.toFixed(2)}h of that is overtime.`
      : "";
  if (elapsedLabel) {
    return `Tracked ${elapsedLabel} · ${logged}h logged (rounded to quarter hours).${overtimeNote} Your program lead will approve them.`;
  }
  return `${logged} hours logged.${overtimeNote} Your program lead will approve them.`;
}

export function ClockControl() {
  const { shouldUseClock } = useVolunteerSelfClockAccess();

  const { data, isLoading } = useGetActiveShiftQuery(undefined, {
    skip: !shouldUseClock,
  });
  const [clockIn, { isLoading: starting }] = useClockInMutation();
  const [clockOut, { isLoading: stopping }] = useClockOutMutation();
  const [pauseClock, { isLoading: pausing }] = usePauseClockMutation();
  const [resumeClock, { isLoading: resuming }] = useResumeClockMutation();

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(id);
  }, []);

  if (!shouldUseClock) return null;

  if (isLoading && !data) {
    return (
      <span className="text-xs text-muted-foreground" aria-hidden data-testid="clock-control-loading">
        …
      </span>
    );
  }

  const running = data?.shift;
  const canClockIn = data?.can_clock_in;

  if (!running && !canClockIn) {
    return null;
  }

  const allocated = data?.allocated_hours_per_shift
    ? Number.parseFloat(data.allocated_hours_per_shift)
    : null;
  const workedLabel = running?.started_at
    ? formatWorkedElapsed(
        running.started_at,
        now,
        running.break_seconds ?? 0,
        running.paused_at,
      )
    : null;
  const workedHours = running?.started_at
    ? (() => {
        const start = new Date(running.started_at).getTime();
        const end = running.paused_at ? new Date(running.paused_at).getTime() : now;
        return Math.max(0, end - start - (running.break_seconds ?? 0) * 1000) / 3_600_000;
      })()
    : 0;
  const overAllocation = allocated !== null && workedHours > allocated;
  const onBreak = Boolean(running?.paused_at);

  const start = async () => {
    try {
      await clockIn({}).unwrap();
      setNow(Date.now());
      void message.success(
        data?.program_name
          ? `Tracking started on ${data.program_name}.`
          : "Tracking started.",
      );
    } catch (e) {
      void message.error(errorMessage(e, "Could not start the clock."));
    }
  };

  const stop = async () => {
    try {
      const shift = await clockOut({}).unwrap();
      void message.success(clockOutMessage(shift, workedLabel));
    } catch (e) {
      void message.error(errorMessage(e, "Could not stop the clock."));
    }
  };

  const pause = async () => {
    try {
      await pauseClock().unwrap();
      void message.success("Break started. Worked time is paused.");
    } catch (e) {
      void message.error(errorMessage(e, "Could not pause the clock."));
    }
  };

  const resume = async () => {
    try {
      await resumeClock().unwrap();
      setNow(Date.now());
      void message.success("Break ended. Tracking again.");
    } catch (e) {
      void message.error(errorMessage(e, "Could not resume the clock."));
    }
  };

  if (!running) {
    return (
      <button
        type="button"
        onClick={() => void start()}
        disabled={starting}
        title={
          data?.program_name
            ? `Start tracking against ${data.program_name}`
            : "Clock in"
        }
        className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
      >
        <Play className="size-3.5" aria-hidden="true" />
        {starting ? "Starting…" : "Clock in"}
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <span
        className={
          overAllocation
            ? "inline-flex items-center gap-1.5 rounded-md bg-warning/15 px-2 py-1 text-xs tabular-nums text-warning-foreground"
            : "inline-flex items-center gap-1.5 rounded-md bg-primary/10 px-2 py-1 text-xs tabular-nums text-primary"
        }
        title={
          onBreak
            ? "On a break. Worked time is paused."
            : overAllocation
              ? `Past the ${allocated?.toFixed(2)}h allocated for a shift. The extra counts as overtime.`
              : data?.program_name
                ? `Tracking against ${data.program_name}`
                : "Tracking time"
        }
      >
        <span className="relative flex size-1.5">
          <span
            className={
              overAllocation
                ? "absolute inline-flex size-full animate-ping rounded-full bg-warning-foreground/60 motion-reduce:animate-none"
                : "absolute inline-flex size-full animate-ping rounded-full bg-primary/60 motion-reduce:animate-none"
            }
          />
          <span
            className={
              overAllocation
                ? "relative inline-flex size-1.5 rounded-full bg-warning-foreground"
                : "relative inline-flex size-1.5 rounded-full bg-primary"
            }
          />
        </span>
        {workedLabel}
      </span>

      {onBreak ? (
        <button
          type="button"
          onClick={() => void resume()}
          disabled={resuming}
          title="End break and keep tracking"
          className="inline-flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs text-foreground hover:bg-muted disabled:opacity-50"
        >
          <Play className="size-3" aria-hidden="true" />
          {resuming ? "Resuming…" : "Resume"}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => void pause()}
          disabled={pausing}
          title="Pause for a break"
          className="inline-flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs text-foreground hover:bg-muted disabled:opacity-50"
        >
          <Pause className="size-3" aria-hidden="true" />
          {pausing ? "Pausing…" : "Pause"}
        </button>
      )}

      <button
        type="button"
        onClick={() => void stop()}
        disabled={stopping}
        title="Clock out"
        className="inline-flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs text-foreground hover:bg-muted disabled:opacity-50"
      >
        <Square className="size-3" aria-hidden="true" />
        {stopping ? "Stopping…" : "Clock out"}
      </button>
    </div>
  );
}

/** Page-level clock controls for the Time Tracker. */
export function TrackTimePanel() {
  const { shouldUseClock } = useVolunteerSelfClockAccess();

  const { data, isLoading } = useGetActiveShiftQuery(undefined, {
    skip: !shouldUseClock,
  });
  const [clockIn, { isLoading: starting }] = useClockInMutation();
  const [clockOut, { isLoading: stopping }] = useClockOutMutation();
  const [pauseClock, { isLoading: pausing }] = usePauseClockMutation();
  const [resumeClock, { isLoading: resuming }] = useResumeClockMutation();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(id);
  }, []);

  if (!shouldUseClock) return null;

  if (isLoading) {
    return (
      <div className="rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
        Checking shift…
      </div>
    );
  }

  const running = data?.shift;
  const programName = data?.program_name ?? null;
  const blockedReason = data?.blocked_reason;

  const start = async () => {
    try {
      await clockIn({}).unwrap();
      setNow(Date.now());
      void message.success(
        programName ? `Tracking started on ${programName}.` : "Tracking started.",
      );
    } catch (e) {
      void message.error(errorMessage(e, "Could not start the clock."));
    }
  };

  const stop = async () => {
    const elapsedLabel = running?.started_at
      ? formatWorkedElapsed(
          running.started_at,
          now,
          running.break_seconds ?? 0,
          running.paused_at,
        )
      : null;
    try {
      const shift = await clockOut({}).unwrap();
      void message.success(clockOutMessage(shift, elapsedLabel));
    } catch (e) {
      void message.error(errorMessage(e, "Could not stop the clock."));
    }
  };

  const pause = async () => {
    try {
      await pauseClock().unwrap();
      void message.success("Break started. Worked time is paused.");
    } catch (e) {
      void message.error(errorMessage(e, "Could not pause the clock."));
    }
  };

  const resume = async () => {
    try {
      await resumeClock().unwrap();
      setNow(Date.now());
      void message.success("Break ended. Tracking again.");
    } catch (e) {
      void message.error(errorMessage(e, "Could not resume the clock."));
    }
  };

  if (!running && !data?.can_clock_in) {
    return (
      <div className="rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
        {blockedReason ??
          "No shift is scheduled for you today. Hours can only be tracked against an assigned shift."}
      </div>
    );
  }

  if (!running) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-3">
        <div>
          <p className="text-sm font-medium">Ready to track time</p>
          <p className="text-xs text-muted-foreground">
            {programName
              ? `Clock in for ${programName} when your shift starts.`
              : "Clock in to start tracking your time."}
          </p>
        </div>
        <button
          type="button"
          onClick={() => void start()}
          disabled={starting}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:brightness-95 disabled:opacity-50"
        >
          <Play className="size-4" aria-hidden />
          {starting ? "Starting…" : "Track time"}
        </button>
      </div>
    );
  }

  const onBreak = Boolean(running.paused_at);
  const workedLabel = formatWorkedElapsed(
    running.started_at!,
    now,
    running.break_seconds ?? 0,
    running.paused_at,
  );

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3">
      <div>
        <p className="text-sm font-medium text-primary">
          {onBreak ? "On a break" : "Tracking time"}
        </p>
        <p className="text-xs text-muted-foreground">
          {programName ? `${programName} · ` : ""}{workedLabel} elapsed
          {onBreak ? " · paused" : ""}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {onBreak ? (
          <button
            type="button"
            onClick={() => void resume()}
            disabled={resuming}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:brightness-95 disabled:opacity-50"
          >
            <Play className="size-4" aria-hidden />
            {resuming ? "Resuming…" : "Resume"}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => void pause()}
            disabled={pausing}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
          >
            <Pause className="size-4" aria-hidden />
            {pausing ? "Pausing…" : "Pause"}
          </button>
        )}
        <button
          type="button"
          onClick={() => void stop()}
          disabled={stopping}
          className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
        >
          <Square className="size-4" aria-hidden />
          {stopping ? "Stopping…" : "Clock out"}
        </button>
      </div>
    </div>
  );
}
